// LeverageEnhancementQueue.jsx
import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "../../utils/axiosInstance";
import ReactPaginate from "react-paginate";
import { toast } from "react-toastify";
import { Loader2, RefreshCw, ExternalLink } from "lucide-react";

const scoreNum = (v) => {
    if (v === null || v === undefined || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};

const fmtScore = (v) => {
    const n = scoreNum(v);
    if (n === null) return "—";
    // keep it readable; backend likely gives 0-1 or 0-100, so just show as-is with up to 2dp
    return Number.isInteger(n) ? String(n) : n.toFixed(2);
};

const domainHref = (raw) => {
    const d = String(raw || "").trim();
    if (!d) return null;
    return d.startsWith("http://") || d.startsWith("https://") ? d : `https://${d}`;
};

export default function LeverageEnhancementQueue() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);

    const [currentPage, setCurrentPage] = useState(0);
    const [sortField, setSortField] = useState("missed_opportunity_risk");
    const [sortOrder, setSortOrder] = useState("desc");
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);

    const recordsPerPage = 30;

    const fetchQueue = async (pageToLoad = currentPage + 1) => {
        const safePage =
            typeof pageToLoad === "number" && Number.isFinite(pageToLoad)
                ? pageToLoad
                : currentPage + 1;
        try {
            setLoading(true);
            const resp = await axiosInstance.get("/queue/leverage-enhancement-queue", {
                params: {
                    status: "pending",
                    page: safePage,
                    page_size: recordsPerPage,
                    sort_by: sortField || "missed_opportunity_risk",
                    sort_order: sortOrder || "desc",
                },
            });
            const payload = resp?.data || {};
            setRows(Array.isArray(payload?.items) ? payload.items : []);
            setTotalPages(Number(payload?.total_pages || 1));
            setTotalRecords(Number(payload?.total || 0));
        } catch (e) {
            console.error(e);
            toast.error("Failed to fetch leverage enhancement queue.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchQueue(currentPage + 1);
    }, [currentPage, sortField, sortOrder]);

    const processed = useMemo(() => {
        return rows || [];
    }, [rows]);

    const pageCount = totalPages || 0;
    const displayed = processed;

    const handleSort = (field) => {
        if (sortField !== field) {
            setSortField(field);
            setSortOrder("asc");
        } else if (sortOrder === "asc") {
            setSortOrder("desc");
        } else {
            // cycle off if you want; keep consistent with existing behaviour
            setSortField(null);
            setSortOrder("asc");
        }
        setCurrentPage(0);
    };

    const columns = [
        { label: "Domain", field: "domain" },
        { label: "Company", field: "company_name" },
        { label: "Business Category", field: "business_category" },
        { label: "Country", field: "country" },
        { label: "Missed Opportunity Risk", field: "missed_opportunity_risk" },
    ];

    const cardClass = "rounded-2xl border border-slate-200 bg-white shadow-sm";
    const subtleBtn =
        "inline-flex h-11 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-800 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-900/20 disabled:opacity-50";

    const headerNoteClass =
        "rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between";

    const linkPill =
        "group inline-flex max-w-full items-center gap-2 rounded-full bg-slate-50 px-2.5 py-1 text-sm font-semibold text-slate-900 ring-1 ring-inset ring-slate-200 hover:bg-slate-100";

    return (
        <>
            {/* Controls (simple: refresh only) */}
            <div className={`${cardClass} p-4`}>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                        <div className="text-sm text-slate-600">
                            Total records: <span className="font-semibold text-slate-800">{totalRecords}</span>
                        </div>
                        <div className="mt-1 text-xs text-slate-500">
                            Sort:{" "}
                            <span className="font-semibold text-slate-700">
                                {sortField ? `${sortField} (${sortOrder})` : "none"}
                            </span>
                        </div>
                    </div>

                    <button onClick={() => fetchQueue(currentPage + 1)} disabled={loading} className={subtleBtn} aria-busy={loading}>
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                        Refresh
                    </button>
                </div>
            </div>

            {/* Note */}
            <div className={`mt-4 ${headerNoteClass}`}>
                <div className="flex items-center gap-2">
                    <span className="inline-flex h-7 items-center rounded-full bg-slate-900 px-2.5 text-xs font-bold text-white">
                        Note
                    </span>
                    <span>
                        Ordered by <b>missed opportunity risk</b> (highest first).
                    </span>
                </div>
            </div>

            {/* Desktop table */}
            <div className={`mt-4 hidden md:block ${cardClass} overflow-hidden`}>
                <div className="overflow-x-auto">
                    <table className="w-full table-auto border-collapse">
                        <thead className="bg-slate-50">
                            <tr className="border-b border-slate-200">
                                {columns.map((col) => (

                                    <th
                                        key={col.field}
                                        onClick={() => handleSort(col.field)}
                                        className="px-4 py-3 text-left text-xs font-extrabold uppercase tracking-wide text-slate-700 cursor-pointer select-none hover:bg-slate-100"
                                    >
                                        <div className="flex items-center gap-2">
                                            <span>{col.label}</span>
                                            <span className="text-slate-400">
                                                {sortField === col.field ? (sortOrder === "asc" ? "↑" : "↓") : ""}
                                            </span>
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>

                        <tbody className="divide-y divide-slate-100">
                            {displayed.length ? (
                                displayed.map((rec, idx) => (
                                    <tr key={`${rec.domain || "row"}-${idx}`} className="hover:bg-slate-50">
                                        {columns.map((col, cIdx) => {
                                            const v = rec?.[col.field];
                                            const tight = cIdx < 2 ? "max-w-[280px] truncate" : "";
                                            if (col.field === "domain") {
                                                const href = domainHref(v);
                                                const label = String(v || "").trim();
                                                return (
                                                    <td key={col.field} className={`px-4 py-3 text-sm ${tight}`}>
                                                        {href ? (
                                                            <a
                                                                href={href}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className={linkPill}
                                                                title={label}
                                                            >
                                                                <span className="truncate">{label}</span>
                                                                <ExternalLink className="h-4 w-4 text-slate-400 group-hover:text-slate-600" />
                                                            </a>
                                                        ) : (
                                                            <span className="text-slate-500">—</span>
                                                        )}
                                                    </td>
                                                );
                                            }
                                            if (col.field === "missed_opportunity_risk") {
                                                return (
                                                    <td key={col.field} className="px-4 py-3 text-sm text-slate-900 font-semibold">
                                                        {fmtScore(v)}
                                                    </td>
                                                );
                                            }
                                            return (
                                                <td key={col.field} className={`px-4 py-3 text-sm text-slate-800 ${tight}`}>
                                                    {v ?? "—"}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td className="px-4 py-10 text-center text-sm text-slate-500" colSpan={columns.length}>
                                        {loading ? (
                                            <span className="inline-flex items-center gap-2">
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                Loading...
                                            </span>
                                        ) : (
                                            "No data"
                                        )}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Mobile cards */}
            <div className="mt-4 block md:hidden space-y-3">
                {displayed.length ? (
                    displayed.map((rec, idx) => {
                        const href = domainHref(rec.domain);
                        const label = String(rec.domain || "").trim();

                        return (
                            <div key={`${rec.domain || "row"}-${idx}`} className={`${cardClass} p-4`}>
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        {href ? (
                                            <a
                                                href={href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="group inline-flex max-w-full items-center gap-2 truncate text-sm font-extrabold text-slate-900 hover:underline"
                                                title={label}
                                            >
                                                <span className="truncate">{label}</span>
                                                <ExternalLink className="h-4 w-4 shrink-0 text-slate-400 group-hover:text-slate-600" />
                                            </a>
                                        ) : (
                                            <div className="truncate text-sm font-extrabold text-slate-900">—</div>
                                        )}
                                        <div className="mt-1 text-sm text-slate-700 truncate">{rec.company_name || "—"}</div>
                                    </div>

                                    <div className="shrink-0 text-right">
                                        <div className="text-xs font-semibold text-slate-500">Risk</div>
                                        <div className="text-sm font-extrabold text-slate-900">{fmtScore(rec.missed_opportunity_risk)}</div>
                                    </div>
                                </div>

                                <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                                    <div className="col-span-2">
                                        <div className="text-xs font-semibold text-slate-500">Business category</div>
                                        <div className="text-slate-800 truncate">{rec.business_category || "—"}</div>
                                    </div>
                                    <div className="col-span-2">
                                        <div className="text-xs font-semibold text-slate-500">Country</div>
                                        <div className="text-slate-800 truncate">{rec.country || "—"}</div>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <div className={`${cardClass} p-8 text-center text-sm text-slate-500`}>
                        {loading ? (
                            <span className="inline-flex items-center gap-2">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Loading...
                            </span>
                        ) : (
                            "No data"
                        )}
                    </div>
                )}
            </div>

            {/* Pagination */}
            {pageCount > 1 && (
                <ReactPaginate
                    previousLabel={<span className="px-3 py-1">← Prev</span>}
                    nextLabel={<span className="px-3 py-1">Next →</span>}
                    pageCount={pageCount}
                    onPageChange={({ selected }) => setCurrentPage(selected)}
                    containerClassName="flex justify-center items-center mt-6 gap-2"
                    pageClassName="px-3 py-1 border border-slate-200 rounded-xl bg-white text-slate-700 hover:bg-slate-50"
                    activeClassName="!bg-slate-900 !text-white !border-slate-900 shadow-sm"
                    disabledClassName="opacity-50 cursor-not-allowed"
                    breakClassName="px-3 py-1 text-slate-500"
                    renderOnZeroPageCount={null}
                />
            )}
        </>
    );
}
